/**
 * HubMed Admin API: supports camelCase (current contract) and PascalCase (legacy).
 */

export const hmNum = (v, fallback = 0) => {
  const n = Number(v ?? fallback);
  return Number.isFinite(n) ? n : fallback;
};

/** Onboarding list row → shape expected by HubMed onboarding DataGrid. */
export function normalizeOnboardingListRow(r) {
  if (!r) return null;
  return {
    ChemistID: r.chemistId ?? r.ChemistID,
    ChemistUUID: r.chemistUuid ?? r.ChemistUUID,
    ChemistName: r.chemistName ?? r.ChemistName,
    Phone: r.phone ?? r.Phone,
    Email: r.email ?? r.Email,
    StripeSubStatus: r.stripeSubStatus ?? r.StripeSubStatus,
    OnboardingID: r.onboardingId ?? r.OnboardingID,
    IsHidden:
      r.isHidden === true ||
      r.IsHidden === true ||
      Number(r.isHidden ?? r.IsHidden ?? 0) === 1
        ? 1
        : 0,
    CommentCount: r.commentCount ?? r.CommentCount,
    LatestCommentDateTime: r.latestCommentDateTime ?? r.LatestCommentDateTime,
  };
}

function normalizeOnboardingComment(c) {
  if (!c) return null;
  return {
    CommentID: c.commentId ?? c.CommentID,
    UserID: c.userId ?? c.UserID,
    Comment: c.comment ?? c.Comment,
    AddedDateTime: c.addedDateTime ?? c.AddedDateTime,
    UserDisplayName: c.userDisplayName ?? c.UserDisplayName,
    UserEmail: c.userEmail ?? c.UserEmail,
  };
}

/** GET/POST onboarding details → shape expected by drawer / timeline. */
export function normalizeOnboardingDetails(d) {
  if (!d) return null;
  const comments = d.comments ?? d.Comments ?? [];
  return {
    ChemistID: d.chemistId ?? d.ChemistID,
    ChemistUUID: d.chemistUuid ?? d.ChemistUUID,
    ChemistName: d.chemistName ?? d.ChemistName,
    Phone: d.phone ?? d.Phone,
    Email: d.email ?? d.Email,
    OnboardingID: d.onboardingId ?? d.OnboardingID,
    IsHidden:
      d.isHidden === true ||
      d.IsHidden === true ||
      Number(d.isHidden ?? d.IsHidden ?? 0) === 1
        ? 1
        : 0,
    Comments: comments.map(normalizeOnboardingComment).filter(Boolean),
  };
}

/** GET AppointmentOverview → canonical camelCase shape for React state. */
export function normalizeAppointmentOverview(raw) {
  if (!raw) return null;
  const s = raw.summary ?? raw.Summary ?? {};
  return {
    summary: {
      averageWaitTimeMinutes:
        s.averageWaitTimeMinutes ?? s.AverageWaitTimeMinutes ?? null,
      averageAppointmentsPerWeek:
        s.averageAppointmentsPerWeek ?? s.AverageAppointmentsPerWeek ?? null,
      totalAppointmentsInRange:
        s.totalAppointmentsInRange ?? s.TotalAppointmentsInRange ?? null,
      totalActiveChemists:
        s.totalActiveChemists ??
        s.TotalActiveChemists ??
        s.activeChemistCount ??
        s.ActiveChemistCount ??
        null,
      providerId: s.providerId ?? s.ProviderId ?? null,
    },
    consultTypeBreakdownRange:
      raw.consultTypeBreakdownRange ?? raw.ConsultTypeBreakdownRange ?? [],
    appointmentsPerDay:
      raw.appointmentsPerDay ?? raw.AppointmentsPerDay ?? [],
    dateRange: raw.dateRange ?? raw.DateRange ?? null,
  };
}

/** GET ConnectedChemists */
export function normalizeConnectedChemistsResponse(raw) {
  if (!raw) return { chemists: [], dateRange: null, providerId: null };
  return {
    chemists: raw.chemists ?? raw.Chemists ?? [],
    dateRange: raw.dateRange ?? raw.DateRange ?? null,
    providerId: raw.providerId ?? raw.ProviderId ?? null,
  };
}

/** GET /Practitioners row → stable grid row */
export function normalizePractitionerListRow(p) {
  if (!p) return null;
  const first = p.firstName ?? p.FirstName ?? "";
  const last = p.lastName ?? p.LastName ?? "";
  const full =
    (p.fullName ?? p.FullName ?? "").trim() ||
    `${first} ${last}`.trim() ||
    "—";
  return {
    id: p.doctorId ?? p.DoctorID,
    doctorId: p.doctorId ?? p.DoctorID,
    firstName: first,
    lastName: last,
    fullName: full,
    practitionerId: p.practitionerId ?? p.PractitionerId ?? p.PractitionerID,
    halaxyProfileId: p.halaxyProfileId ?? p.HalaxyProfileID ?? p.HalaxyProfileId,
    isCannabis: Boolean(p.isCannabis ?? p.IsCannabis),
    isADHD: Boolean(p.isADHD ?? p.IsADHD),
    isADHD_NSW: Boolean(p.isADHD_NSW ?? p.IsADHD_NSW ?? p.isAdhd_Nsw),
    isActive: p.isActive ?? p.IsActive,
  };
}

/** POST UpdatePractitionerFlags body or nested `practitioner` in response */
export function normalizePractitionerFromResponse(resOrPractitioner) {
  const p =
    resOrPractitioner?.practitioner ??
    resOrPractitioner?.Practitioner ??
    resOrPractitioner;
  return normalizePractitionerListRow(p);
}
